/**
 * Live HTTP implementation of the B1.9 Invoice feature API.
 *
 * Targets the Phase D backend surface at `/api/biz/invoices/*`
 * (see `backend/src/api/business/biz-invoice-routes.ts`).
 *
 * Contract alignment:
 *   The backend service (`backend/src/domains/business/biz-invoice-service.ts`)
 *   hydrates rows into the EXACT camelCase shape the FE already consumes
 *   (InvoiceRow / InvoiceListSnapshot), so no field remapping is needed.
 *   The only transformation performed here is:
 *     • query-string serialization for list filters
 *     • mapping of `ApiError` → feature-local error taxonomy (keeps the
 *       React Query mutation types narrow)
 *
 * Endpoints:
 *   listInvoices      GET  /biz/invoices?branchId&status&type&sort&...
 *   getInvoice        GET  /biz/invoices/:id       (detail, not yet wired
 *                                                   to useInvoice hooks —
 *                                                   reserved for drawer)
 *   issueInvoice      POST /biz/invoices/:id/issue
 *   sendInvoice       POST /biz/invoices/:id/send
 *   voidInvoice       POST /biz/invoices/:id/void
 *   downloadInvoice   — 501; Phase E (integrator + PDF storage)
 *
 * @see invoice-api-mock.ts (deterministic fixture path)
 * @see invoice-api.ts (mock↔live dispatcher)
 */

import { ApiError } from '@/shared/api/apiClient';
import { bizGet, bizPost } from '@/shared/api/bizClient';

import type {
  DownloadInvoiceRequest,
  InvoiceListFilters,
  InvoiceListSnapshot,
  InvoiceRow,
  IssueInvoiceRequest,
  ListInvoicesRequest,
  SendInvoiceRequest,
  VoidInvoiceRequest,
} from './types';

// ---------------------------------------------------------------------------
// Query serializer
// ---------------------------------------------------------------------------

function serializeListFilters(
  req: ListInvoicesRequest,
): Record<string, string | undefined> {
  const filters = (req.filters ?? {}) as Partial<InvoiceListFilters>;
  const params: Record<string, string | undefined> = {
    branchId: req.branchId || undefined,
  };
  if (filters.search && filters.search.trim().length > 0) {
    params['search'] = filters.search.trim();
  }
  if (filters.status && filters.status.length > 0) {
    params['status'] = filters.status.join(',');
  }
  if (filters.type && filters.type.length > 0) {
    params['type'] = filters.type.join(',');
  }
  if (filters.customerId) {
    params['customerId'] = filters.customerId;
  }
  if (filters.dateRange) {
    params['from'] = filters.dateRange.from;
    params['to'] = filters.dateRange.to;
  }
  if (filters.sort) {
    params['sort'] = filters.sort.field;
    params['dir'] = filters.sort.dir;
  }
  if (typeof filters.page === 'number') {
    params['page'] = String(filters.page);
  }
  if (typeof filters.pageSize === 'number') {
    params['pageSize'] = String(filters.pageSize);
  }
  return params;
}

// ---------------------------------------------------------------------------
// Error mapping — surface backend error codes without leaking HTTP plumbing
// ---------------------------------------------------------------------------

function translate(err: unknown, fallback: string): never {
  if (err instanceof ApiError) {
    const detail = err.payload.detail ?? err.payload.title;
    // Preserve original ApiError semantics (.status, .correlationId) for
    // higher layers (React Query onError), but ensure .message reflects
    // the richer detail when the backend provided one.
    if (detail && err.message !== detail) {
      (err as { message: string }).message = detail;
    }
    throw err;
  }
  throw new Error(`${fallback}: ${(err as Error)?.message ?? String(err)}`);
}

// ---------------------------------------------------------------------------
// Public API (HTTP-backed; 1:1 surface parity with invoice-api-mock.ts)
// ---------------------------------------------------------------------------

export async function listInvoices(
  req: ListInvoicesRequest,
): Promise<InvoiceListSnapshot> {
  try {
    return await bizGet<InvoiceListSnapshot>('/biz/invoices', {
      params: serializeListFilters(req),
    });
  } catch (err) {
    translate(err, 'listInvoices failed');
  }
}

export async function getInvoiceSnapshot(
  branchId: string,
  filters?: Partial<InvoiceListFilters>,
): Promise<InvoiceListSnapshot> {
  return listInvoices({ branchId, filters: filters ?? {} });
}

export async function getInvoice(invoiceId: string): Promise<InvoiceRow> {
  try {
    return await bizGet<InvoiceRow>(`/biz/invoices/${invoiceId}`);
  } catch (err) {
    translate(err, 'getInvoice failed');
  }
}

export async function issueInvoice(
  req: IssueInvoiceRequest,
): Promise<InvoiceRow> {
  try {
    return await bizPost<InvoiceRow, { idempotencyKey: string }>(
      `/biz/invoices/${req.invoiceId}/issue`,
      { idempotencyKey: req.idempotencyKey },
    );
  } catch (err) {
    translate(err, 'issueInvoice failed');
  }
}

export async function sendInvoice(
  req: SendInvoiceRequest,
): Promise<InvoiceRow> {
  try {
    const body: Record<string, string> = {
      channel: req.channel,
      target: req.target,
    };
    if (req.note) body['note'] = req.note;
    return await bizPost<InvoiceRow>(
      `/biz/invoices/${req.invoiceId}/send`,
      body,
    );
  } catch (err) {
    translate(err, 'sendInvoice failed');
  }
}

export async function voidInvoice(
  req: VoidInvoiceRequest,
): Promise<InvoiceRow> {
  try {
    const body: Record<string, string> = { reason: req.reason };
    if (req.explanation) body['explanation'] = req.explanation;
    return await bizPost<InvoiceRow>(
      `/biz/invoices/${req.invoiceId}/void`,
      body,
    );
  } catch (err) {
    translate(err, 'voidInvoice failed');
  }
}

/**
 * Phase E: PDF / UBL-XML download is not yet backed by a real endpoint.
 * The backend intentionally returns 501 on `/download` until the integrator
 * adapter ships. The FE surfaces this as a friendly "not available yet"
 * error without crashing the list page.
 */
export async function downloadInvoice(
  _req: DownloadInvoiceRequest,
): Promise<{ url: string; filename: string }> {
  throw new ApiError(
    {
      type: 'petota:biz-invoice/download-not-implemented',
      title: 'Invoice download is not available yet',
      status: 501,
      detail:
        'PDF ve UBL-XML indirme Phase E ile aktifleşecek. Şimdilik önizleme ve paylaşım bağlantısını kullanabilirsiniz.',
    },
    501,
  );
}
